import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UpdatedFact } from '@/components/product-insights/fab-ui';
import {
  PiDeniedPage,
  PiError,
  PiFilterChips,
  PiPageLayout,
  PiSearch,
  PiToolbar,
} from '@/components/product-insights/pi-kit';
import { useFabFilters } from '@/components/product-insights/use-fab-data';
import { VisaTutorClientsTable } from '@/components/product-insights/visa-tutor/VisaTutorClientsTable';
import {
  VisaTutorDestinations,
  VisaTutorFunnel,
  VisaTutorScores,
  VisaTutorStats,
} from '@/components/product-insights/visa-tutor/VisaTutorOverview';
import { useVisaTutorSummary } from '@/components/product-insights/visa-tutor/use-visa-tutor-data';
import {
  destinationOptions,
  syncedText,
  type DestinationOption,
} from '@/components/product-insights/visa-tutor/visa-tutor-model';
import { LayoutGrid, LayoutToolbar } from '@/components/layout';
import { Select } from '@/components/ui';
import { ApiError } from '@/lib/api';
import { formatDateOnly } from '@/lib/format';
import { useAuth } from '@/lib/use-auth';
import { usePageLayout, type LayoutBlockDefault } from '@/lib/use-page-layout';

/**
 * /product-insights/visa-tutor — AI Visa Tutor, owner 2026-10-18. A client
 * school or university buys AI mock visa-interview licences for its admitted
 * students; this page answers what each client's licences delivered. Live read
 * through EMERGE's `/product-insights/visa-tutor` proxy; nothing stored.
 * Built on the shared Product Insights template (`pi-kit`, 2026-10-19).
 *
 * URL state (every default is an absent key; nothing written on arrival):
 * | Key       | Meaning                                       | Default            |
 * |-----------|-----------------------------------------------|--------------------|
 * | `cycle`   | SEED business cycle id (SEED FAB's list)      | SEED FAB's current |
 * | `country` | student destination, ISO 3166-1 alpha-2       | every destination  |
 * | `q`       | client / university / company search (local) | ''                 |
 *
 * - The read waits for the cycle list to resolve the current cycle, so the
 *   default view is that cycle. Cycles are SEED's, the same ids SEED FAB uses.
 * - Without `product-insights` read nothing is fetched: the refusal renders
 *   before the data component mounts.
 * - `meta.sourceLastSyncedAt` is a fact about the data ("Synced 10:42"), shown
 *   in the facts line; `UpdatedFact` is when EMERGE fetched it.
 * - Choosing a destination country (toolbar or the destination list) narrows
 *   the whole read on the server, so every tile, the funnel and the table
 *   describe students heading there. The toolbar's destination options are
 *   remembered from the last unfiltered read, since a filtered read only
 *   carries the chosen country.
 * - Layout manager (2026-10-20): toolbar and tiles are fixed; the funnel,
 *   average scores, destination list and clients table are movable blocks of
 *   `product-insights.visa-tutor`, saved per person. Never rename a block id.
 */

const LAYOUT_DEFAULTS: readonly LayoutBlockDefault[] = [
  { id: 'funnel', label: 'Licence funnel' },
  { id: 'scores', label: 'Average scores', width: 'half' },
  { id: 'destinations', label: 'Licences by destination country', width: 'half' },
  { id: 'clients', label: 'Clients' },
];

const CYCLE_PARAM = 'cycle';
const COUNTRY_PARAM = 'country';
const SEARCH_PARAM = 'q';
const SEARCH_DEBOUNCE_MS = 250;
const ALL_DESTINATIONS = '';

function parseCycle(raw: string | null): number | null {
  if (raw === null || !/^\d+$/.test(raw)) return null;
  const n = Number.parseInt(raw, 10);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

function parseCountry(raw: string | null): string | null {
  if (raw === null) return null;
  const code = raw.trim().toUpperCase();
  return /^[A-Z]{2}$/.test(code) ? code : null;
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message || fallback;
  return fallback;
}

export function ProductInsightsVisaTutorPage() {
  const { ability } = useAuth();
  if (!ability.canRead('product-insights')) {
    return <PiDeniedPage product="visa-tutor" title="AI Visa Tutor" />;
  }
  return <VisaTutorContent />;
}

function VisaTutorContent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlCycleId = parseCycle(searchParams.get(CYCLE_PARAM));
  const country = parseCountry(searchParams.get(COUNTRY_PARAM));
  const urlSearch = searchParams.get(SEARCH_PARAM) ?? '';
  const layout = usePageLayout('product-insights.visa-tutor', LAYOUT_DEFAULTS);

  /* --- debounced search ------------------------------------------------ */

  const [draftSearch, setDraftSearch] = useState(urlSearch);
  const [seenUrlSearch, setSeenUrlSearch] = useState(urlSearch);
  if (seenUrlSearch !== urlSearch) {
    setSeenUrlSearch(urlSearch);
    setDraftSearch(urlSearch);
  }

  useEffect(() => {
    if (draftSearch === urlSearch) return;
    const timer = window.setTimeout(() => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (draftSearch.trim() === '') params.delete(SEARCH_PARAM);
          else params.set(SEARCH_PARAM, draftSearch);
          return params;
        },
        { replace: true },
      );
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [draftSearch, urlSearch, setSearchParams]);

  /* --- data ------------------------------------------------------------- */

  const filtersResult = useFabFilters(urlCycleId);
  const { filters } = filtersResult;
  const reloadFilters = filtersResult.reload;

  const selectedCycleId = urlCycleId ?? filters?.resolvedCycleId ?? null;
  const selectedCycle = useMemo(
    () => filters?.cycles.find((c) => c.id === selectedCycleId) ?? null,
    [filters, selectedCycleId],
  );

  const [reloadToken, setReloadToken] = useState(0);
  const summary = useVisaTutorSummary(selectedCycleId, country, reloadToken);

  const refresh = useCallback(() => setReloadToken((n) => n + 1), []);
  const retry = useCallback(() => {
    reloadFilters();
    setReloadToken((n) => n + 1);
  }, [reloadFilters]);

  // Destination options from the last read that was not narrowed to a country.
  const [knownDestinations, setKnownDestinations] = useState<DestinationOption[]>([]);
  const [seenSummaryData, setSeenSummaryData] = useState(summary.data);
  if (seenSummaryData !== summary.data) {
    setSeenSummaryData(summary.data);
    if (summary.data !== null && country === null) {
      setKnownDestinations(destinationOptions(summary.data.data.byCountry, null));
    }
  }

  /* --- filters ---------------------------------------------------------- */

  const updateParams = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          mutate(params);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const onCycleChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const id = Number.parseInt(event.target.value, 10);
      if (!Number.isSafeInteger(id)) return;
      updateParams((params) => {
        // The resolved (current) cycle is the default: an absent key.
        if (filters && id === filters.resolvedCycleId) params.delete(CYCLE_PARAM);
        else params.set(CYCLE_PARAM, String(id));
      });
    },
    [filters, updateParams],
  );

  const setCountry = useCallback(
    (next: string | null) => {
      updateParams((params) => {
        if (next === null) params.delete(COUNTRY_PARAM);
        else params.set(COUNTRY_PARAM, next);
      });
    },
    [updateParams],
  );

  const onCountryChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const next = event.target.value;
      setCountry(next === ALL_DESTINATIONS ? null : next);
    },
    [setCountry],
  );

  const onSelectCountry = useCallback((iso2: string) => setCountry(iso2), [setCountry]);

  const resetFilters = useCallback(() => {
    setDraftSearch('');
    updateParams((params) => {
      params.delete(CYCLE_PARAM);
      params.delete(COUNTRY_PARAM);
      params.delete(SEARCH_PARAM);
    });
  }, [updateParams]);

  /* --- render ----------------------------------------------------------- */

  const meta = summary.data?.meta ?? null;
  const synced = syncedText(meta?.sourceLastSyncedAt);
  const fetchedAt = meta?.fetchedAt ?? meta?.generatedAt ?? null;
  const pageError = filtersResult.error ?? summary.error;
  const dataLoading = summary.loading || (selectedCycleId === null && filters === null);

  const destinations = useMemo(() => {
    if (country === null || knownDestinations.some((d) => d.code === country)) {
      return knownDestinations;
    }
    const fromData = destinationOptions(summary.data?.data.byCountry ?? [], country);
    return [...knownDestinations, ...fromData.filter((d) => d.code === country)].sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  }, [country, knownDestinations, summary.data]);

  const countryChipLabel =
    country === null ? null : (destinations.find((d) => d.code === country)?.label ?? country);

  const chips: { key: string; label: string; onRemove: () => void }[] = [];
  if (urlCycleId !== null && selectedCycle !== null) {
    chips.push({
      key: 'cycle',
      label: `Cycle: ${selectedCycle.name}`,
      onRemove: () => updateParams((params) => params.delete(CYCLE_PARAM)),
    });
  }
  if (country !== null && countryChipLabel !== null) {
    chips.push({
      key: 'country',
      label: `Destination: ${countryChipLabel}`,
      onRemove: () => setCountry(null),
    });
  }
  if (urlSearch.trim() !== '') {
    chips.push({
      key: 'search',
      label: `Search: ${urlSearch.trim()}`,
      onRemove: () => {
        setDraftSearch('');
        updateParams((params) => params.delete(SEARCH_PARAM));
      },
    });
  }

  const facts =
    selectedCycle !== null || synced !== null ? (
      <>
        {selectedCycle !== null && (
          <>
            {formatDateOnly(selectedCycle.startDate)} – {formatDateOnly(selectedCycle.endDate)}
          </>
        )}
        {selectedCycle !== null && synced !== null && ' · '}
        {synced !== null && meta?.sourceLastSyncedAt && (
          <>
            Synced{' '}
            <time dateTime={meta.sourceLastSyncedAt} className="tabular-nums">
              {synced}
            </time>
          </>
        )}
      </>
    ) : undefined;

  return (
    <PiPageLayout
      product="visa-tutor"
      title="AI Visa Tutor"
      facts={facts}
      actions={
        <>
          <UpdatedFact fetchedAt={fetchedAt} refreshing={summary.refreshing} onRefresh={refresh} />
          <LayoutToolbar layout={layout} />
        </>
      }
    >
      <PiToolbar
        chips={
          chips.length > 0 ? <PiFilterChips chips={chips} onReset={resetFilters} /> : undefined
        }
      >
        <div className="w-48 max-sm:w-full">
          <Select
            aria-label="Cycle"
            value={selectedCycleId !== null ? String(selectedCycleId) : ''}
            onChange={onCycleChange}
            disabled={!filters}
          >
            {!filters && <option value="">Cycle</option>}
            {filters?.cycles.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.isCurrent ? `${c.name} (current)` : c.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="w-48 max-sm:w-full">
          <Select
            aria-label="Destination"
            value={country ?? ALL_DESTINATIONS}
            onChange={onCountryChange}
            disabled={destinations.length === 0 && country === null}
          >
            <option value={ALL_DESTINATIONS}>All destinations</option>
            {destinations.map((d) => (
              <option key={d.code} value={d.code}>
                {d.label}
              </option>
            ))}
          </Select>
        </div>
        <PiSearch
          value={draftSearch}
          onChange={setDraftSearch}
          label="Search clients"
          className="w-full sm:w-64"
        />
      </PiToolbar>

      {pageError !== null && pageError !== undefined && (
        <PiError
          message={errorMessage(pageError, 'AI Visa Tutor could not be reached.')}
          onRetry={retry}
        />
      )}

      {(summary.data !== null || pageError === null || pageError === undefined) && (
        <>
          <VisaTutorStats data={summary.data} loading={dataLoading} />
          <LayoutGrid
            layout={layout}
            render={{
              funnel: () => <VisaTutorFunnel data={summary.data} loading={dataLoading} />,
              scores: () => <VisaTutorScores data={summary.data} loading={dataLoading} />,
              destinations: () => (
                <VisaTutorDestinations
                  data={summary.data}
                  loading={dataLoading}
                  onSelectCountry={country === null ? onSelectCountry : undefined}
                />
              ),
              clients: () => (
                <VisaTutorClientsTable
                  data={summary.data}
                  loading={summary.refreshing}
                  search={draftSearch}
                />
              ),
            }}
          />
        </>
      )}
    </PiPageLayout>
  );
}

export default ProductInsightsVisaTutorPage;
